import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  PhoneCall,
  MessageSquare,
  Mail,
  Workflow,
  CheckCircle2,
  Bot,
  Calendar,
  ArrowRight,
  Volume2,
  ChevronDown,
  Sparkles,
  Layers,
  Database,
  ShieldCheck,
  Zap,
  TrendingUp,
  Clock
} from "lucide-react";
import { useLanguage } from "./LanguageContext";

export default function AIAutomationServices() {
  const { t } = useLanguage();

  // Active Tab for Capability Showcase
  const [activeTab, setActiveTab] = useState<"voice" | "whatsapp" | "email" | "omni" | "support">("voice");

  // ROI Estimator State
  const [monthlyInquiries, setMonthlyInquiries] = useState(300);
  const [avgDealValue, setAvgDealValue] = useState(750);
  const [currentMissedRate, setCurrentMissedRate] = useState(30);

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // ROI calculations
  const missedInquiriesCaptured = Math.round(monthlyInquiries * (currentMissedRate / 100));
  const additionalConversions = Math.round(missedInquiriesCaptured * 0.35);
  const estMonthlyRevenueRecovered = additionalConversions * avgDealValue;
  const hoursSavedPerMonth = Math.round(monthlyInquiries * 0.4);

  const scrollToContact = () => {
    const contactElem = document.getElementById("contact");
    if (contactElem) {
      contactElem.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.hash = "#contact";
    }
  };

  // Structured FAQ Data for SEO & AI GEO Engine Optimization
  const faqs = [
    {
      q: t("faqQ1", "automations") || "How does an Autonomous AI Voice Agent handle inbound phone calls for a business?",
      a: t("faqA1", "automations") || "An Autonomous AI Voice Agent uses ultra-low latency (<280ms) natural speech synthesis and large language models to converse naturally with human callers. It answers FAQs from your company documentation, checks live staff calendars (Google Workspace/Outlook), books appointments, and warm-transfers high-priority calls directly to staff mobile devices."
    },
    {
      q: t("faqQ2", "automations") || "What is the speed-to-lead advantage of AI Business Automations?",
      a: t("faqA2", "automations") || "Contacting an inbound lead within 60 seconds yields a 391% higher conversion rate compared to a 30-minute delay. An Autonomous AI Ecosystem responds instantly (<1 second) across Phone, WhatsApp, and Email—ensuring zero lead decay and maximum revenue capture."
    },
    {
      q: t("faqQ3", "automations") || "Can WhatsApp Business AI Assistants send PDF catalogs and book meetings inside chat?",
      a: t("faqA3", "automations") || "Yes! Utilizing the official Meta WhatsApp Business Cloud API, our WhatsApp AI Assistants deliver interactive product cards, price quotes, audio voice notes, and instant calendar booking widgets directly inside WhatsApp."
    },
    {
      q: t("faqQ4", "automations") || "How does the AI Automation Engine sync with CRMs like HubSpot, Salesforce, and Zoho?",
      a: t("faqA4", "automations") || "The automation system operates via bi-directional real-time webhooks and REST API integrations. Every phone call transcript, voice recording, WhatsApp chat summary, and email thread is instantly structured into lead profile records and deal pipeline stages in your CRM."
    },
    {
      q: t("faqQ5", "automations") || "Is customer data secure and compliant?",
      a: t("faqA5", "automations") || "Data transit is encrypted using enterprise-grade TLS 1.3 and AES-256 encryption at rest. The architecture adheres to SOC-2 Type II standards, HIPAA guidelines, and GDPR/CCPA privacy frameworks. Your company documentation and customer transcripts are never used to train public LLM models."
    },
    {
      q: t("faqQ6", "automations") || "How quickly can a business deploy a custom AI Voice & Operations Engine?",
      a: t("faqA6", "automations") || "Deployment takes under 48 hours. Our engineering team ingests your existing company documentation, connects your phone numbers and CRM APIs, tests edge cases in a staging sandbox, and launches your 24/7 autonomous ecosystem seamlessly."
    }
  ];

  // SEO & AI GEO JSON-LD Schema
  const jsonLdServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Teralumax Autonomous AI Business Operations & Receptionist Engine",
    "serviceType": "AI Telephony Voice Agent, WhatsApp Commerce, Email Triage & CRM Automation",
    "provider": {
      "@type": "Organization",
      "name": "Teralumax Private Limited",
      "url": "https://www.teralumax.com"
    },
    "areaServed": "Worldwide",
    "description": "24/7 Autonomous AI agents for inbound phone call answering, WhatsApp Business Cloud API commerce, zero-delay email triage, and bi-directional CRM synchronization."
  };

  const jsonLdFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <section 
      id="automation-services" 
      className="relative py-20 lg:py-28 bg-[#FAFAFC] text-slate-900 border-b border-slate-200/80 overflow-hidden"
    >
      {/* Inject Structured Data JSON-LD Scripts for Search & AI Answer Engine Indexing */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdServiceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaqSchema) }} />

      {/* Background Decorative Mesh */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-indigo-100/60 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        
        {/* Section Header */}
        <header className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-2xs mb-4"
          >
            <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" />
            <span className="text-xs font-mono font-bold tracking-wider text-indigo-700 uppercase">
              {t("badge", "automations") || "AUTONOMOUS BUSINESS OPERATIONS & AI GROWTH ENGINE"}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4 leading-tight"
          >
            {t("title", "automations") || "Stop Losing Inbound Revenue to Response Lag & Unanswered Calls"}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="text-base sm:text-lg text-slate-600 font-normal leading-relaxed"
          >
            {t("subtitle", "automations") || "Deploy 24/7 human-grade AI agents across Voice Calls, WhatsApp, Email, and CRM workflows—capturing, qualifying, and booking appointments automatically."}
          </motion.p>
        </header>

        {/* Multilingual Navigation Tab Bar */}
        <nav aria-label="AI Services Navigation" className="flex justify-center mb-10">
          <div className="inline-flex p-1 bg-white border border-slate-200 rounded-2xl shadow-2xs gap-1 max-w-full overflow-x-auto scrollbar-none">
            {[
              { id: "voice", label: t("tabVoice", "automations") || "Autonomous Voice Agent", icon: PhoneCall, color: "bg-indigo-600" },
              { id: "whatsapp", label: t("tabWhatsapp", "automations") || "WhatsApp Commerce", icon: MessageSquare, color: "bg-emerald-600" },
              { id: "email", label: t("tabEmail", "automations") || "Zero-Delay Email Triage", icon: Mail, color: "bg-blue-600" },
              { id: "omni", label: t("tabOmni", "automations") || "CRM & Workflow Sync", icon: Workflow, color: "bg-violet-600" },
              { id: "support", label: t("tabSupport", "automations") || "24/7 Support Desk", icon: Bot, color: "bg-amber-600" }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? `${tab.color} text-white shadow-xs`
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Capability Showcase Content */}
        <AnimatePresence mode="wait">
          
          {/* TAB 1: VOICE AGENT SHOWCASE */}
          {activeTab === "voice" && (
            <motion.article
              key="voice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16"
            >
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-5 font-bold">
                    <PhoneCall size={20} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 mb-3">
                    {t("voiceTitle", "automations") || "Turn Inbound Phone Calls Into Confirmed Revenue 24/7"}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {t("voiceDesc", "automations") || "Eliminate missed calls and voicemail lead decay. Our human-sounding Voice AI answers on the first ring, answers complex business questions using your documentation, checks real-time calendar availability, and books qualified appointments on the spot."}
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      t("voiceFeature1", "automations") || "Responds in under 280ms with human-grade voice prosody & accent adaptability",
                      t("voiceFeature2", "automations") || "Checks live staff schedules on Google & Outlook to book sales calls directly",
                      t("voiceFeature3", "automations") || "Warm-transfers high-priority VIP buyers directly to on-call team mobile phones",
                      t("voiceFeature4", "automations") || "Automatically logs call recordings, audio transcripts, and sentiment scores in your CRM"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                        <CheckCircle2 size={15} className="text-indigo-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100 text-center font-mono">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("voiceStat1", "automations") || "LATENCY"}
                    </span>
                    <span className="text-xs font-bold text-indigo-600">
                      {t("voiceStat1Val", "automations") || "< 280ms"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("voiceStat2", "automations") || "ANSWER RATE"}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">
                      {t("voiceStat2Val", "automations") || "100% Guaranteed"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("voiceStat3", "automations") || "CAPACITY"}
                    </span>
                    <span className="text-xs font-bold text-slate-900">
                      {t("voiceStat3Val", "automations") || "10,000+ Calls"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Static Full Telephony Call Transcript Showcase */}
              <div className="lg:col-span-6 bg-slate-900 text-white p-6 sm:p-8 rounded-2xl border border-slate-800 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-6">
                    <div className="flex items-center gap-2">
                      <Volume2 size={16} className="text-indigo-400" />
                      <span className="text-xs font-mono font-bold text-slate-300 uppercase">
                        {t("voiceBoxTitle", "automations") || "LIVE TELEPHONY CALL TRANSCRIPT"}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono bg-indigo-950 text-indigo-300 px-2 py-0.5 rounded border border-indigo-800 font-bold">
                      {t("voiceBoxStatus", "automations") || "24/7 ACTIVE TELEPHONY NODE"}
                    </span>
                  </div>

                  {/* Audio Waveform Visual Graphic */}
                  <div className="flex items-center justify-center gap-1.5 py-3 bg-slate-950 border border-slate-800/80 rounded-xl mb-4">
                    {[40, 70, 30, 90, 60, 100, 45, 80, 55, 30, 85, 50, 70, 35].map((h, i) => (
                      <div
                        key={i}
                        style={{ height: `${h}%` }}
                        className="w-1 bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-full h-8"
                      />
                    ))}
                  </div>

                  {/* Complete Full Dialogue Transcript */}
                  <div className="space-y-3 font-mono text-xs">
                    <div className="p-3 bg-indigo-950/80 rounded-xl border border-indigo-900/60 text-indigo-200">
                      <strong className="text-indigo-400 block text-[10px] uppercase font-bold mb-0.5">
                        {t("voiceSpeakerAgent", "automations") || "AI Voice Agent"}:
                      </strong>
                      "{t("voiceMsg1", "automations") || "Hello! Thank you for calling Teralumax. I'm your AI Assistant. How can I assist your team today?"}"
                    </div>

                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-slate-300">
                      <strong className="text-slate-400 block text-[10px] uppercase font-bold mb-0.5">
                        {t("voiceSpeakerCaller", "automations") || "Inbound Lead"}:
                      </strong>
                      "{t("voiceMsg2", "automations") || "Hi! We're losing inbound sales leads on weekends. Can your AI handle phone inquiries and book enterprise demos?"}"
                    </div>

                    <div className="p-3 bg-indigo-950/80 rounded-xl border border-indigo-900/60 text-indigo-200">
                      <strong className="text-indigo-400 block text-[10px] uppercase font-bold mb-0.5">
                        {t("voiceSpeakerAgent", "automations") || "AI Voice Agent"}:
                      </strong>
                      "{t("voiceMsg3", "automations") || "Absolutely! I answer questions using your documentation, check live staff calendars, and schedule confirmed demos instantly. Shall I book a slot for tomorrow at 2:00 PM EST?"}"
                    </div>

                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-slate-300">
                      <strong className="text-slate-400 block text-[10px] uppercase font-bold mb-0.5">
                        {t("voiceSpeakerCaller", "automations") || "Inbound Lead"}:
                      </strong>
                      "{t("voiceMsg4", "automations") || "Yes, tomorrow at 2:00 PM EST works perfectly!"}"
                    </div>

                    <div className="p-3 bg-emerald-950/90 rounded-xl border border-emerald-800 text-emerald-200">
                      <strong className="text-emerald-400 block text-[10px] uppercase font-bold mb-0.5">
                        {t("voiceSpeakerAgent", "automations") || "AI Voice Agent"}:
                      </strong>
                      "{t("voiceMsg5", "automations") || "Done! I've reserved tomorrow at 2:00 PM EST on Google Calendar, created your lead profile in HubSpot, and emailed a calendar invite."}"
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-300">
                  <div className="flex items-center gap-2">
                    <Calendar size={15} className="text-indigo-400" />
                    <span className="font-medium">
                      {t("voiceConfirmed", "automations") || "Auto-Booked to Google Calendar & HubSpot CRM • CONFIRMED"}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-emerald-400 font-bold bg-emerald-950 px-2.5 py-0.5 rounded border border-emerald-800">
                    SYNCED
                  </span>
                </div>
              </div>
            </motion.article>
          )}

          {/* TAB 2: WHATSAPP COMMERCE SHOWCASE */}
          {activeTab === "whatsapp" && (
            <motion.article
              key="whatsapp"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16"
            >
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5 font-bold">
                    <MessageSquare size={20} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 mb-3">
                    {t("waTitle", "automations") || "Turn WhatsApp Messages Into Instant Sales & Bookings"}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {t("waDesc", "automations") || "Capitalize on WhatsApp's 98% open rate. Engage inbound leads immediately, deliver interactive PDF catalogs, qualify buyer budgets automatically, and secure sales consultations inside WhatsApp without human friction."}
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      t("waFeature1", "automations") || "Official Meta WhatsApp Business Cloud API green-badge verified integration",
                      t("waFeature2", "automations") || "Sends interactive product cards, price quotes, audio voice notes & booking buttons",
                      t("waFeature3", "automations") || "Automated B2B lead scoring and budget qualification algorithms",
                      t("waFeature4", "automations") || "Seamless live-human escalation dashboard for high-value sales reps"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                        <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100 text-center font-mono">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("waStat1", "automations") || "SPEED TO LEAD"}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">
                      {t("waStat1Val", "automations") || "< 1 Second"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("waStat2", "automations") || "OPEN RATE"}
                    </span>
                    <span className="text-xs font-bold text-slate-900">
                      {t("waStat2Val", "automations") || "98.4%"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("waStat3", "automations") || "CONVERSION"}
                    </span>
                    <span className="text-xs font-bold text-indigo-600">
                      {t("waStat3Val", "automations") || "3.8x Growth"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Static Full WhatsApp Conversation Showcase */}
              <div className="lg:col-span-6 bg-[#efeae2] p-5 rounded-2xl border border-slate-300 shadow-2xs flex flex-col justify-between">
                <div>
                  {/* WhatsApp App Header */}
                  <div className="bg-[#075e54] text-white p-3 rounded-xl flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-white text-[#075e54] flex items-center justify-center font-bold text-xs">
                        <Bot size={16} />
                      </div>
                      <div>
                        <span className="text-xs font-bold block">
                          {t("waAgentHeader", "automations") || "Teralumax WhatsApp Agent"}
                        </span>
                        <span className="text-[10px] text-emerald-200 block">
                          {t("waAgentStatus", "automations") || "Online • Instant Reply"}
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono bg-emerald-800 px-2 py-0.5 rounded text-emerald-100 font-bold">
                      {t("waMetaBadge", "automations") || "Meta Cloud API"}
                    </span>
                  </div>

                  {/* Full Message Thread */}
                  <div className="space-y-3 text-xs">
                    {/* User Msg 1 */}
                    <div className="flex flex-col items-end">
                      <div className="p-3 rounded-xl max-w-[88%] bg-[#dcf8c6] text-slate-900 shadow-2xs">
                        <p>{t("waMsg1", "automations") || "Hi! Can an AI Voice & WhatsApp agent handle our inbound leads and book meetings automatically?"}</p>
                        <span className="text-[9px] text-slate-500 block text-right mt-1 font-mono">10:42 AM</span>
                      </div>
                    </div>

                    {/* Bot Msg 1 */}
                    <div className="flex flex-col items-start">
                      <div className="p-3 rounded-xl max-w-[88%] bg-white text-slate-900 shadow-2xs">
                        <p>{t("waMsg2", "automations") || "Hello! Yes, absolutely. Our AI responds in under 1 second via Meta WhatsApp Business API. It qualifies buyer budgets, shares interactive product catalogs, and books meetings on your calendar."}</p>
                        <span className="text-[9px] text-slate-400 block text-right mt-1 font-mono">10:42 AM</span>
                      </div>
                    </div>

                    {/* User Msg 2 */}
                    <div className="flex flex-col items-end">
                      <div className="p-3 rounded-xl max-w-[88%] bg-[#dcf8c6] text-slate-900 shadow-2xs">
                        <p>{t("waMsg3", "automations") || "How fast does it integrate with our CRM like HubSpot or Salesforce?"}</p>
                        <span className="text-[9px] text-slate-500 block text-right mt-1 font-mono">10:43 AM</span>
                      </div>
                    </div>

                    {/* Bot Msg 2 */}
                    <div className="flex flex-col items-start">
                      <div className="p-3 rounded-xl max-w-[88%] bg-white text-slate-900 shadow-2xs">
                        <p>{t("waMsg4", "automations") || "In under 15 minutes! The AI automatically creates contacts, tags lead intent, logs transcripts, and syncs calendar invites on staff schedules."}</p>
                        <div className="mt-2.5 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-center font-bold font-mono text-[11px]">
                          {t("waActionPill", "automations") || "📅 Book Strategy Audit (Auto-Synced)"}
                        </div>
                        <span className="text-[9px] text-slate-400 block text-right mt-1 font-mono">10:43 AM</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-300/80 flex items-center justify-between text-[11px] font-mono text-slate-600">
                  <span>Verified Meta Green Badge</span>
                  <span className="text-emerald-700 font-bold">100% Automated</span>
                </div>
              </div>
            </motion.article>
          )}

          {/* TAB 3: EMAIL TRIAGE SHOWCASE */}
          {activeTab === "email" && (
            <motion.article
              key="email"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16"
            >
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5 font-bold">
                    <Mail size={20} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 mb-3">
                    {t("emailTitle", "automations") || "Zero Inbox Lag: Instant Proposal & Lead Processing Engine"}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {t("emailDesc", "automations") || "Transform slow email response times into instant competitive advantages. Automatically classify incoming inquiries, generate contextually accurate draft replies, and attach direct calendar invites before competitors even open their inbox."}
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      t("emailFeature1", "automations") || "Eliminates response lag—replies instantly 24 hours a day, 365 days a year",
                      t("emailFeature2", "automations") || "AI intent classifier categorizes Sales, VIP, Billing, and Support inquiries",
                      t("emailFeature3", "automations") || "Synthesizes company knowledge bases, FAQs, and price matrices into personalized replies",
                      t("emailFeature4", "automations") || "Instantly alerts sales reps on Slack/Teams when high-value enterprise leads write in"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                        <CheckCircle2 size={15} className="text-blue-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100 text-center font-mono">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("emailStat1", "automations") || "INBOX DELAY"}
                    </span>
                    <span className="text-xs font-bold text-blue-600">
                      {t("emailStat1Val", "automations") || "0 Minutes"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("emailStat2", "automations") || "TIME SAVED"}
                    </span>
                    <span className="text-xs font-bold text-slate-900">
                      {t("emailStat2Val", "automations") || "15+ Hrs/Wk"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("emailStat3", "automations") || "ACCURACY"}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">
                      {t("emailStat3Val", "automations") || "99.2%"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Static Full Email Triage Showcase */}
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-slate-150 mb-4">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-blue-600" />
                      <span className="text-xs font-bold text-slate-900 font-mono uppercase">
                        {t("emailBoxHeader", "automations") || "INBOX TRIAGE ENGINE"}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-bold">
                      {t("emailBoxStatus", "automations") || "0 MINS DELAY"}
                    </span>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="flex justify-between font-bold text-slate-900 mb-1">
                        <span>{t("emailFrom", "automations") || "From: director@enterprise.com"}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{t("emailTime", "automations") || "Just now"}</span>
                      </div>
                      <span className="font-semibold text-blue-600 block mb-1">
                        {t("emailSubject", "automations") || "Subject: Custom AI implementation inquiry"}
                      </span>
                      <p className="text-slate-600">
                        {t("emailBody", "automations") || "\"Looking to automate inbound phone calls and email triage for our 50+ sales reps.\""}
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono">
                      <div className="p-2 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 font-bold">
                        {t("emailStep1", "automations") || "1. Intent Classified"}
                      </div>
                      <div className="p-2 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 font-bold">
                        {t("emailStep2", "automations") || "2. Knowledge Synthesized"}
                      </div>
                      <div className="p-2 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 font-bold">
                        {t("emailStep3", "automations") || "3. Calendar Linked"}
                      </div>
                    </div>

                    <div className="p-3.5 bg-slate-900 text-slate-100 rounded-xl font-mono text-[11px] space-y-1.5">
                      <span className="text-[10px] text-emerald-400 block font-bold uppercase">
                        {t("emailReplyTitle", "automations") || "AUTOMATED RESPONSE GENERATED"}
                      </span>
                      <p className="text-slate-300">
                        {t("emailReplyBody", "automations") || "\"Hi Director! Based on your team size, our Enterprise Architecture fits seamlessly. I've reserved a strategy slot for you below.\""}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>
                    {t("emailIntentLabel", "automations") || "Buying Intent:"} <strong className="text-emerald-600">{t("emailIntentVal", "automations") || "High (98/100)"}</strong>
                  </span>
                  <span>
                    {t("emailRoutedLabel", "automations") || "Routed to:"} <strong className="text-slate-900">{t("emailRoutedVal", "automations") || "HubSpot + Slack"}</strong>
                  </span>
                </div>
              </div>
            </motion.article>
          )}

          {/* TAB 4: CRM & WORKFLOW SHOWCASE */}
          {activeTab === "omni" && (
            <motion.article
              key="omni"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16"
            >
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-5 font-bold">
                    <Workflow size={20} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 mb-3">
                    {t("omniTitle", "automations") || "Zero Manual Entry: Bi-Directional CRM & Workflow Orchestration"}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {t("omniDesc", "automations") || "Unify every phone call, WhatsApp conversation, and email thread into your core enterprise tech stack. Automatically create contacts, update pipeline deals, log notes, and trigger post-interaction SMS & email follow-up drips."}
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      t("omniFeature1", "automations") || "50+ pre-built native integrations: HubSpot, Salesforce, Zoho, Google Sheets, Slack, Zapier",
                      t("omniFeature2", "automations") || "Real-time webhook pipelines for proprietary SQL databases & internal ERPs",
                      t("omniFeature3", "automations") || "Automated post-interaction SMS & WhatsApp nurturing drip campaigns",
                      t("omniFeature4", "automations") || "Instant team notifications across Slack, Microsoft Teams, and SMS"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                        <CheckCircle2 size={15} className="text-violet-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100 text-center font-mono">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("omniStat1", "automations") || "CONNECTORS"}
                    </span>
                    <span className="text-xs font-bold text-violet-600">
                      {t("omniStat1Val", "automations") || "50+ Native"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("omniStat2", "automations") || "DATA SYNC"}
                    </span>
                    <span className="text-xs font-bold text-slate-900">
                      {t("omniStat2Val", "automations") || "Real-Time (0s)"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("omniStat3", "automations") || "ERRORS"}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">
                      {t("omniStat3Val", "automations") || "0% Manual"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Workflow Pipeline Graphic */}
              <div className="lg:col-span-6 bg-slate-900 text-white p-6 sm:p-8 rounded-2xl border border-slate-800 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-6">
                    <span className="text-xs font-mono font-bold text-slate-300 uppercase">
                      {t("omniBoxHeader", "automations") || "BI-DIRECTIONAL SYNC PIPELINE"}
                    </span>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950 px-2 py-0.5 rounded border border-emerald-800 font-bold">
                      {t("omniBoxStatus", "automations") || "LIVE STREAM"}
                    </span>
                  </div>

                  <div className="space-y-3 font-mono text-xs">
                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PhoneCall size={15} className="text-indigo-400" />
                        <span>{t("omniStep1Title", "automations") || "Inbound Call / WhatsApp Completed"}</span>
                      </div>
                      <span className="text-slate-400 text-[10px] font-bold uppercase">{t("omniStep1Tag", "automations") || "Trigger"}</span>
                    </div>

                    <div className="w-px h-3 bg-slate-700 mx-auto" />

                    <div className="p-3 bg-indigo-950/80 rounded-xl border border-indigo-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Layers size={15} className="text-indigo-300" />
                        <span>{t("omniStep2Title", "automations") || "AI Transcript & Sentiment Extraction"}</span>
                      </div>
                      <span className="text-indigo-300 text-[10px] font-bold uppercase">{t("omniStep2Tag", "automations") || "Processing"}</span>
                    </div>

                    <div className="w-px h-3 bg-slate-700 mx-auto" />

                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Database size={15} className="text-emerald-400" />
                        <span>{t("omniStep3Title", "automations") || "HubSpot Deal Created & Assigned"}</span>
                      </div>
                      <span className="text-emerald-400 text-[10px] font-bold uppercase">{t("omniStep3Tag", "automations") || "Synced (0s)"}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-[11px] font-mono text-slate-400">
                  <span>{t("omniCrmList", "automations") || "Supported CRMs: HubSpot, Salesforce, Zoho, Google Sheets"}</span>
                </div>
              </div>
            </motion.article>
          )}

          {/* TAB 5: 24/7 SUPPORT SHOWCASE */}
          {activeTab === "support" && (
            <motion.article
              key="support"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16"
            >
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5 font-bold">
                    <Bot size={20} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 mb-3">
                    {t("supportTitle", "automations") || "Autonomous 24/7 Customer Support & Desk Triage"}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {t("supportDesc", "automations") || "Delight customers with instant 24/7 answers to technical FAQs, order status inquiries, and billing questions. Cut support operational costs by up to 70% while improving your CSAT scores."}
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      t("supportFeature1", "automations") || "Resolves 80%+ of tier-1 support inquiries autonomously without human staff intervention",
                      t("supportFeature2", "automations") || "Integrated with Shopify, Zendesk, Intercom, and custom database knowledge bases",
                      t("supportFeature3", "automations") || "Smart escalation routes complex edge cases to human support reps with full context summaries",
                      t("supportFeature4", "automations") || "Multilingual fluency across 50+ global languages for international business reach"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                        <CheckCircle2 size={15} className="text-amber-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100 text-center font-mono">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("supportStat1", "automations") || "RESOLVED"}
                    </span>
                    <span className="text-xs font-bold text-amber-600">
                      {t("supportStat1Val", "automations") || "80%+ Instant"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("supportStat2", "automations") || "SAVINGS"}
                    </span>
                    <span className="text-xs font-bold text-emerald-600">
                      {t("supportStat2Val", "automations") || "Up to 70%"}
                    </span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      {t("supportStat3", "automations") || "CSAT"}
                    </span>
                    <span className="text-xs font-bold text-slate-900">
                      {t("supportStat3Val", "automations") || "98.7%"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Support Card Graphic */}
              <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-slate-150 mb-4">
                    <span className="text-xs font-bold text-slate-900 font-mono uppercase">
                      {t("supportBoxHeader", "automations") || "SUPPORT DESK TICKET RESOLUTION"}
                    </span>
                    <span className="text-[10px] font-mono text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      {t("supportBoxStatus", "automations") || "AUTONOMOUS"}
                    </span>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <span className="text-[10px] font-mono text-slate-400 block mb-1 uppercase font-bold">
                        {t("supportQueryLabel", "automations") || "Customer Query:"}
                      </span>
                      <p className="text-slate-800 font-medium">
                        {t("supportQueryVal", "automations") || "\"Where is my order #TR-8492 and can I update my shipping address?\""}
                      </p>
                    </div>

                    <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-900">
                      <span className="text-[10px] font-bold block mb-1 uppercase text-amber-700">
                        {t("supportResLabel", "automations") || "AI Resolution (< 1s):"}
                      </span>
                      <p>
                        {t("supportResVal", "automations") || "\"Order #TR-8492 is out for delivery. Address updated in Shopify and notification sent via SMS!\""}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>
                    {t("supportLangLabel", "automations") || "Languages:"} <strong>{t("supportLangVal", "automations") || "50+ Supported"}</strong>
                  </span>
                  <span>
                    {t("supportStatusLabel", "automations") || "Ticket Status:"} <strong className="text-emerald-600">{t("supportStatusVal", "automations") || "Resolved"}</strong>
                  </span>
                </div>
              </div>
            </motion.article>
          )}

        </AnimatePresence>

        {/* Clean Multilingual Revenue Recovery Calculator */}
        <section aria-label="ROI Revenue Calculator" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-2xs mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs font-mono font-bold text-indigo-600 uppercase tracking-wider block mb-1">
              {t("roiTag", "automations") || "REVENUE RECOVERY ESTIMATOR"}
            </span>
            <h3 className="font-display text-2xl font-bold text-slate-900 mb-2">
              {t("roiTitle", "automations") || "Calculate Your Monthly Inbound Revenue Recovery"}
            </h3>
            <p className="text-slate-600 text-xs sm:text-sm">
              {t("roiSub", "automations") || "See how much revenue an Autonomous AI Agent recovers by capturing missed calls & delayed inquiries instantly."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Calculator Sliders */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2 text-xs font-mono font-bold">
                  <span className="text-slate-700 uppercase">
                    {t("roiLabelInquiries", "automations") || "Monthly Inbound Inquiries"}
                  </span>
                  <span className="text-indigo-600">{monthlyInquiries} / month</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="2000"
                  step="25"
                  value={monthlyInquiries}
                  onChange={(e) => setMonthlyInquiries(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2 text-xs font-mono font-bold">
                  <span className="text-slate-700 uppercase">
                    {t("roiLabelDealValue", "automations") || "Average Customer Deal Value ($)"}
                  </span>
                  <span className="text-indigo-600">${avgDealValue}</span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="5000"
                  step="50"
                  value={avgDealValue}
                  onChange={(e) => setAvgDealValue(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2 text-xs font-mono font-bold">
                  <span className="text-slate-700 uppercase">
                    {t("roiLabelMissedRate", "automations") || "Estimated Missed/Delayed Leads (%)"}
                  </span>
                  <span className="text-amber-600">{currentMissedRate}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="60"
                  step="5"
                  value={currentMissedRate}
                  onChange={(e) => setCurrentMissedRate(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-amber-600"
                />
              </div>
            </div>

            {/* Calculated Results Cards */}
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
                <span className="text-[10px] font-mono text-slate-400 font-bold uppercase block mb-1">
                  {t("roiStatLeads", "automations") || "LEADS CAPTURED"}
                </span>
                <span className="text-xl sm:text-2xl font-bold font-mono text-indigo-600 block">
                  +{missedInquiriesCaptured}
                </span>
                <span className="text-[10px] text-slate-500 mt-1 block font-mono">
                  {t("roiStatLeadsUnit", "automations") || "Inquiries / Mo"}
                </span>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
                <span className="text-[10px] font-mono text-slate-400 font-bold uppercase block mb-1">
                  {t("roiStatHours", "automations") || "HOURS SAVED"}
                </span>
                <span className="text-xl sm:text-2xl font-bold font-mono text-slate-900 block">
                  {hoursSavedPerMonth} hrs
                </span>
                <span className="text-[10px] text-slate-500 mt-1 block font-mono">
                  {t("roiStatHoursUnit", "automations") || "Staff Time / Mo"}
                </span>
              </div>

              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                <span className="text-[10px] font-mono text-emerald-800 font-bold uppercase block mb-1">
                  {t("roiStatRevenue", "automations") || "EST. REVENUE RECOVERED"}
                </span>
                <span className="text-xl sm:text-2xl font-bold font-mono text-emerald-600 block">
                  ${estMonthlyRevenueRecovered.toLocaleString()}
                </span>
                <span className="text-[10px] text-emerald-700 mt-1 block font-mono">
                  {t("roiStatRevenueUnit", "automations") || "Recovered / Mo"}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Multilingual FAQ Section + SEO Accordion */}
        <section aria-label="Frequently Asked Questions" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-2xs mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs font-mono font-bold text-indigo-600 uppercase tracking-wider block mb-1">
              {t("faqTag", "automations") || "FREQUENTLY ASKED QUESTIONS"}
            </span>
            <h3 className="font-display text-2xl font-bold text-slate-900">
              {t("faqTitle", "automations") || "AI Business Automations & Receptionist FAQ"}
            </h3>
          </div>

          <div className="space-y-3 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                  aria-expanded={openFaqIndex === idx}
                  className="w-full p-4 text-left bg-slate-50/50 hover:bg-slate-100/50 font-semibold text-slate-900 flex items-center justify-between gap-3 cursor-pointer text-xs sm:text-sm transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={16} className={`text-slate-500 shrink-0 transition-transform ${openFaqIndex === idx ? "rotate-180 text-indigo-600" : ""}`} />
                </button>

                {openFaqIndex === idx && (
                  <div className="p-4 bg-white border-t border-slate-150 text-slate-600 text-xs leading-relaxed">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Multilingual Call To Action Banner */}
        <div className="bg-slate-900 text-white rounded-2xl p-8 sm:p-10 shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-500/20 rounded-full text-indigo-300 text-xs font-mono font-bold uppercase mb-3">
              <Sparkles size={13} />
              {t("ctaTag", "automations") || "48-HOUR DEPLOYMENT"}
            </span>
            <h3 className="font-display text-2xl sm:text-3xl font-bold mb-2">
              {t("ctaTitle", "automations") || "Ready to Automate Your Inbound Operations?"}
            </h3>
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
              {t("ctaSub", "automations") || "Deploy custom AI Voice & Business Automation agents trained on your business documentation in under 48 hours."}
            </p>
          </div>

          <button
            onClick={scrollToContact}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl text-xs sm:text-sm transition-colors shadow-sm cursor-pointer flex items-center justify-center gap-2 shrink-0"
          >
            <span>{t("ctaBtnPrimary", "automations") || "Book AI Architecture Audit"}</span>
            <ArrowRight size={16} />
          </button>
        </div>

      </div>
    </section>
  );
}
